<template>
  <div>
    <Head :title="__('Home')" />
    <div id="home" class="relative overflow-hidden bg-primary pt-[100px] md:pt-[130px] lg:pt-[160px]">
      <div class="container">
        <div class="-mx-4 flex flex-wrap items-center">
          <div class="w-full px-4">
            <div class="hero-content wow fadeInUp mx-auto max-w-[780px] text-center" data-wow-delay=".2s">
              <h1
                class="mb-8 text-3xl font-bold leading-snug text-white sm:text-4xl sm:leading-snug md:text-[45px] md:leading-snug"
                v-html="sanitizeHtml(html.sections[0].title)"
              />
              <p
                class="mx-auto mb-10 max-w-[1200px] text-base text-[#e4e4e4] sm:text-lg sm:leading-relaxed md:text-xl md:leading-relaxed"
                v-html="sanitizeHtml(html.sections[0].details)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <section
      v-if="html.sections[2].enable_ticket_section" id="feedbackSection"
      class="relative md:pt-12 pt-8 pb-12 bg-gray-50 dark:bg-slate-800"
    >
      <div class="container">
        <div class="card rounded shadow-xl overflow-hidden">
          <!-- Tab Navigation -->
          <div class="flex border-b">
            <button
              :class="{
                'bg-primary text-white': activeTab === 'submit',
                'bg-gray-100 text-gray-700 hover:bg-gray-200': activeTab !== 'submit'
              }" class="flex-1 py-4 px-6 font-medium text-center focus:outline-none transition-colors duration-300"
              @click="activeTab = 'submit'; currentStep = 1"
            >
              {{ __('Submit Feedback') }}
            </button>
            <button
              :class="{
                'bg-primary text-white': activeTab === 'track',
                'bg-gray-100 text-gray-700 hover:bg-gray-200': activeTab !== 'track'
              }" class="flex-1 py-4 px-6 font-medium text-center focus:outline-none transition-colors duration-300"
              @click="activeTab = 'track'"
            >
              {{ __('Track Feedback') }}
            </button>
          </div>

          <!-- Tab Content -->
          <div class="p-4">
            <!-- Submit Feedback Form -->
            <div v-show="activeTab === 'submit'">
              <form enctype="multipart/form-data" @submit.prevent="store">
                <div class="px-5">
                  <h1
                    class="py-5 text-center md:text-3xl text-2xl md:leading-normal leading-normal font-semibold"
                  >
                    {{ getStepTitle() }}
                  </h1>
                  <div class="mx-auto mb-6 mt-1 w-24 border-b" />

                  <div v-if="form.errors" class="form-error">
                    {{ form.errors.message }}
                  </div>

                  <!-- Step 1: Type Selection -->
                  <div v-show="currentStep === 1">
                    <h2 class="mb-4 text-xl font-bold">{{ __('Step 1: Feedback Type') }}</h2>

                    <select-input
                      v-model="form.type_id" :error="form.errors.type_id"
                      class="pr-6 rtl:pr-0 rtl:pl-6 pb-5 md:col-span-6 lg:w-1/3" required
                      @change="onTypeChange"
                    >
                      <option :value="null">{{ __('Select a type') }}</option>
                      <option v-for="type in types" :key="type.id" :value="type.id">
                        {{ type.name
                        }}
                      </option>
                    </select-input>

                    <div class="mb-6">
                      <label class="form-label">{{ __('Would you like to submit this anonymously?')
                      }}</label>
                      <div class="flex items-center space-x-4">
                        <label class="inline-flex items-center">
                          <input
                            v-model="form.is_anonymous" type="radio" class="form-radio"
                            name="is_anonymous" :value="true"
                            @change="handleAnonymousChange"
                          />
                          <span class="ml-2">{{ __('Yes') }}</span>
                        </label>
                        <label class="inline-flex items-center">
                          <input
                            v-model="form.is_anonymous" type="radio" class="form-radio"
                            name="is_anonymous" :value="false"
                            @change="handleAnonymousChange"
                          />
                          <span class="ml-2">{{ __('No') }}</span>
                        </label>
                      </div>
                      <div v-if="form.errors.is_anonymous" class="form-error">
                        {{
                          form.errors.is_anonymous }}
                      </div>
                    </div>

                    <!-- Anonymous Warning -->
                    <div
                      v-if="form.is_anonymous === true"
                      class="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400"
                    >
                      <div class="flex">
                        <div class="flex-shrink-0">
                          <svg
                            class="h-5 w-5 text-yellow-400"
                            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </div>
                        <div class="ml-3 text-sm text-yellow-700">
                          <p>{{ __('By submitting anonymously:') }}</p>
                          <ul class="list-disc pl-5 mt-1">
                            <li>
                              {{ __('Your issue will be handled with the same priority')
                              }}
                            </li>
                            <li>
                              {{ __('You will not receive updates or feedback on the resolution') }}
                            </li>
                            <li>
                              {{ __('We will not be able to verify if you were satisfied with the outcome') }}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div class="flex justify-end">
                      <loading-button
                        :loading="form.processing" :disabled="!form.type_id"
                        class="rounded-lg bg-primary py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        @click="nextStep"
                      >
                        {{ __('Next') }}
                      </loading-button>
                    </div>
                  </div>

                  <!-- Step 2: For known submissions - personal details -->
                  <div v-show="currentStep === 2 && !form.is_anonymous">
                    <h2 class="mb-4 text-xl font-bold">{{ __('Step 2: Your Details') }}</h2>

                    <div class="pr-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <text-input
                        v-model="form.first_name" :error="form.errors.first_name"
                        class="w-full md:w-90" :label="__('First Name')"
                      />
                      <text-input
                        v-model="form.last_name" :error="form.errors.last_name"
                        class="w-full md:w-90" :label="__('Last Name')"
                      />
                    </div>

                    <div class="pr-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <text-input
                        v-model="form.email" :error="form.errors.email"
                        class="w-full md:w-90" :label="__('Email Address')" type="email"
                      />
                      <text-input
                        v-model="form.phone_number" :error="form.errors.phone_number"
                        class="w-full md:w-90" :label="__('Phone Number')"
                      />
                    </div>

                    <div class="flex justify-between">
                      <loading-button
                        type="button"
                        class="rounded-lg bg-gray-400 py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                        @click="prevStep"
                      >
                        {{ __('Back') }}
                      </loading-button>
                      <loading-button
                        :loading="form.processing"
                        class="rounded-lg bg-primary py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                        @click="nextStep"
                      >
                        {{ __('Next') }}
                      </loading-button>
                    </div>
                  </div>

                  <!-- Step 2 for anonymous or Step 3 for known: Details -->
                  <div
                    v-show="(currentStep === 2 && form.is_anonymous) || (currentStep === 3 && !form.is_anonymous)"
                  >
                    <h2 class="mb-4 text-xl font-bold">
                      {{ form.is_anonymous ? __('Step 2: Details') : __('Step 3: Details') }}
                    </h2>
                    <div class="pr-6 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <!-- ... details form remains the same but with generic labels ... -->
                      <text-input
                        v-model="form.subject" :error="form.errors.subject"
                        class="pr-6 rtl:pr-0 rtl:pl-6 pb-5 w-full" :label="getSubjectLabel()"
                        required
                      />

                      <div class="mb-4">
                        <label class="form-label">{{ getDateLabel() }}</label>
                        <input
                          v-model="form.date_of_incident" type="date"
                          :error="form.errors.date_of_incident || dateError"
                          class="form-input w-full" required @blur="validateDate"
                        />
                        <div v-if="form.errors.date_of_incident" class="form-error">
                          {{
                            form.errors.date_of_incident }}
                        </div>
                        <div
                          v-if="dateError && !form.errors.date_of_incident"
                          class="form-error"
                        >
                          {{ dateError }}
                        </div>
                      </div>
                    </div>

                    <div class="pr-6 pb-8 w-full">
                      <label class="form-label">{{ getDetailsLabel() }}</label>
                      <ckeditor
                        id="ticketDetails" v-model="form.details" :editor="editor"
                        :config="editorConfig"
                      />
                    </div>

                    <input
                      ref="file" type="file"
                      accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf, .zip"
                      class="hidden" multiple="multiple" @change="fileInputChange"
                    />
                    <div class="pr-6 pb-8 w-full lg:w-full flex-col">
                      <button
                        type="button" class="btn flex justify-center items-center"
                        @click="fileBrowse"
                      >
                        <icon name="file" class="flex-shrink-0 h-8 fill-gray-400 pr-1" />
                        <h4>{{ __('Attach files') }}</h4>
                      </button>

                      <p class="text-sm text-gray-700 dark:text-gray-300 mt-2 font-medium">
                        {{ __('Maximum 5 files, 5MB each, 20MB total') }}
                      </p>
                      <div v-if="form.files.length" class="space-y-2">
                        <div
                          v-for="(file, fi) in form.files" :key="fi"
                          class="flex items-center justify-between p-2 border rounded"
                        >
                          <div class="flex-1 pr-1 flex items-center">
                            <icon
                              name="file"
                              class="flex-shrink-0 h-5 fill-gray-400 mr-2"
                            />
                            <div>
                              <div class="text-sm">{{ file.name }}</div>
                              <div class="text-xs text-gray-500">
                                {{ getFileSize(file.size)
                                }}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button" class="text-red-500 hover:text-red-700"
                            @click="fileRemove(file, fi)"
                          >
                            <icon name="trash" class="h-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="flex justify-between">
                      <loading-button
                        type="button"
                        class="rounded-lg bg-gray-400 py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                        @click="prevStep"
                      >
                        {{ __('Back') }}
                      </loading-button>
                      <loading-button
                        :loading="form.processing"
                        class="rounded-lg bg-primary py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                        @click="nextStep"
                      >
                        {{ __('Next') }}
                      </loading-button>
                    </div>
                  </div>

                  <!-- Step 3 for anonymous or Step 4 for known: Review & Consent -->
                  <div
                    v-show="(currentStep === 3 && form.is_anonymous) || (currentStep === 4 && !form.is_anonymous)"
                  >
                    <h2 class="mb-4 text-xl font-bold">{{ __('Review & Consent') }}</h2>

                    <div class="summary-table mb-6 p-4 border rounded bg-gray-50">
                      <h3 class="font-bold mb-2">
                        {{ __('Summary of your' + ' ' + getTypeName()) }}
                      </h3>

                      <template v-if="!form.is_anonymous">
                        <p>
                          <strong>{{ __('Name:') }}</strong> {{ form.first_name }} {{
                            form.last_name }}
                        </p>
                        <p><strong>{{ __('Email:') }}</strong> {{ form.email }}</p>
                        <p><strong>{{ __('Phone:') }}</strong> {{ form.phone_number }}</p>
                      </template>
                      <template v-else>
                        <p><strong>{{ __('Submitted:') }}</strong> {{ __('Anonymously') }}</p>
                      </template>

                      <p><strong>{{ __('Subject:') }}</strong> {{ form.subject }}</p>
                      <p><strong>{{ __('Date:') }}</strong> {{ form.date_of_incident }}</p>
                      <p style="word-wrap: break-word;">
                        <strong>{{ __('Details:') }}</strong> {{
                          stripHtml(form.details) }}
                      </p>
                      <div v-if="form.files.length">
                        <p><strong>{{ __('Attached Files:') }}</strong></p>
                        <ul>
                          <li v-for="(file, fi) in form.files" :key="fi">
                            {{ file.name }} ({{
                              getFileSize(file.size) }})
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div class="flex items-start mb-5">
                      <div class="flex items-center h-5">
                        <input
                          id="consent_checkbox" v-model="form.consent_checkbox"
                          type="checkbox"
                          class="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800"
                          required
                        />
                      </div>
                      <label
                        for="consent_checkbox"
                        class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        {{ __('I consent to CAAZ collecting and processing this ') }}{{
                          getTypeName().toLowerCase() }}
                        <span v-if="form.is_anonymous">{{ __('(submitted anonymously)')
                        }}</span>
                        <span> - </span>
                        <a
                          :href="route('privacy')"
                          class="text-blue-600 hover:text-blue-800 hover:underline"
                          target="_blank" rel="noopener noreferrer"
                        >
                          {{ __('View our Data Protection Policy') }}
                        </a>
                      </label>
                    </div>

                    <div class="flex justify-between">
                      <loading-button
                        type="button"
                        class="rounded-lg bg-gray-400 py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                        @click="prevStep"
                      >
                        {{ __('Back') }}
                      </loading-button>
                      <loading-button
                        type="submit" :loading="form.processing"
                        class="rounded-lg bg-primary py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                      >
                        {{ getSubmitButtonText() }}
                      </loading-button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <!-- Track Feedback Form -->
            <div v-show="activeTab === 'track'">
              <div class="px-5">
                <h1
                  class="py-5 text-center md:text-3xl text-2xl md:leading-normal leading-normal font-semibold"
                >
                  {{ __('Track Feedback') }}
                </h1>
                <div class="mx-auto mb-6 mt-1 w-24 border-b" />

                <div class="max-w-md mx-auto">
                  <p class="mb-6 text-gray-700 text-center">
                    {{ __('Enter your reference number to check the status of your submission.') }}
                  </p>

                  <div class="mb-6">
                    <label class="block text-gray-700 mb-2">{{ __('Reference Number') }}</label>
                    <input
                      v-model="trackingForm.reference" type="text"
                      class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      :placeholder="__('Enter your reference number')" required maxlength="6"
                    />
                  </div>


                  <LoadingButton
                    :loading="trackingForm.processing"
                    class="w-full rounded-lg bg-primary py-3 px-6 text-base font-medium text-white duration-300 ease-in-out hover:bg-opacity-80"
                    style="justify-content: center;" @click="trackComplaint"
                  >
                    {{ __('Track Feedback') }}
                  </LoadingButton>

                  <div class="mt-6 text-center">
                    <p class="text-gray-600">{{ __("Don't have a reference number?") }}</p>
                    <Link :href="route('contact')" class="text-primary hover:underline">
                      {{ __('Contact our support team') }}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="pt-5 pb-8 lg:pt-[20px] lg:pb-[70px] bg-white">
      <div class="container">
        <div class="-mx-4 flex flex-wrap">
          <div class="w-full px-4">
            <div class="mb-12 max-w-[1400px] lg:mb-20">
              <h2 class="mb-4 text-3xl font-bold text-dark sm:text-4xl md:text-[42px] text-center">
                {{ html.sections[1].title }}
              </h2>
              <p
                class="text-lg leading-relaxed text-body-color sm:text-xl sm:leading-relaxed text-center"
                v-html="html.sections[1].details"
              />
            </div>
          </div>
        </div>
        <div v-if="html.sections[1]" class="-mx-4 flex flex-wrap">
          <div
            v-for="(feature, fi) in html.sections[1].features" :key="fi"
            class="w-full px-4 md:w-1/2 lg:w-1/4"
          >
            <div class="wow fadeInUp group mb-12 bg-white" data-wow-delay=".1s">
              <div
                class="relative z-10 mb-8 flex h-[70px] w-[70px] items-center justify-center rounded-2xl bg-primary"
              >
                <span
                  class="absolute top-0 left-0 z-[-1] mb-8 flex h-[70px] w-[70px] rotate-[25deg] items-center justify-center rounded-2xl bg-primary bg-opacity-20 duration-300 group-hover:rotate-45"
                />
                <icon :name="feature.icon" class="w-8 h-8 fill-white" />
              </div>
              <h4 class="mb-3 text-xl font-bold text-dark">
                {{ feature.title }}
              </h4>
              <p class="mb-8 text-body-color lg:mb-11" v-html="feature.details" />
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Modal for Ticket View -->
    <div v-if="showTicketModal" class="fixed top-24 inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-500 opacity-75" @click="closeModal">
            <a href="route('home')" />
          </div>
        </div>

        <!-- Modal content -->
        <div
          class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
        >
          <!-- Close button -->
          <div class="absolute top-0 right-0 pt-4 pr-4">
            <button
              class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              @click="closeModal"
            >
              <span class="sr-only"><a href="route('home')">Close</a></span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Ticket content -->
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
            <div v-if="loadingFailed" class="flex flex-col items-center justify-center p-4">
              <div class="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
                <svg
                  class="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <h2 class="text-2xl font-bold text-gray-800 mb-4">Oops! Something went wrong</h2>
                <p class="text-gray-600 mb-6">We couldn't load the ticket information.</p>
                <button
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  @click="closeModal"
                >
                  Close
                </button>
              </div>
            </div>

            <div v-else-if="ticket" class="space-y-6">
              <!-- Closed Ticket Banner -->
              <div v-if="ticket.closed" class="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <svg
                      class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20" fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-red-700">
                      This ticket was closed
                      <span v-if="ticket.closed_at"> on {{ formatDate(ticket.closed_at) }}</span>
                      <span v-if="ticket.closed_by"> by {{ ticket.closed_by?.name || 'System'
                      }}</span>
                    </p>
                  </div>
                </div>
              </div>

              <!-- Top Section - Reference and Status -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div class="md:col-span-2">
                  <p class="font-semibold text-gray-700 text-lg mb-1">Reference Number:</p>
                  <p class="text-2xl font-bold text-blue-700 tracking-wide">{{ ticket.uid }}</p>
                </div>
                <div class="md:text-left">
                  <p class="font-semibold text-gray-700 text-lg mb-1">Status:</p>
                  <span
                    :class="getStatusClass(ticket.status.name)"
                    class="inline-flex items-center px-4 py-2 rounded-full text-base font-semibold shadow-sm"
                  >
                    {{ ticket.status.name }}
                  </span>
                </div>
              </div>

              <!-- Subject Section -->
              <div>
                <p class="font-semibold text-gray-700 text-lg mb-1">Subject:</p>
                <p class="text-gray-800 text-lg leading-relaxed">{{ ticket.subject }}</p>
              </div>

              <!-- Date Information -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="font-semibold text-gray-700 text-lg mb-1">Submitted On:</p>
                  <p class="text-gray-800 text-base">{{ formatDateTime(ticket.created_at) }}</p>
                </div>
                <div v-if="ticket.closed">
                  <p class="font-semibold text-gray-700 text-lg mb-1">Closed On:</p>
                  <p class="text-gray-800 text-base">{{ formatDateTime(ticket.closed_at) }}</p>
                </div>
              </div>

              <!-- Details Section -->
              <div>
                <p class="font-semibold text-gray-700 text-lg mb-2">Details:</p>
                <div
                  class="prose max-w-none text-gray-800 text-base leading-relaxed p-4 bg-gray-50 rounded-lg border border-gray-200"
                  v-html="ticket.details"
                />
              </div>

              <!-- Attachments Section -->
              <div v-if="ticket.attachments && ticket.attachments.length > 0">
                <p class="font-semibold text-gray-700 text-lg mb-2">Attachments:</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div
                    v-for="attachment in ticket.attachments" :key="attachment.id"
                    class="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <a
                      :href="`/files/${attachment.path}`" target="_blank"
                      class="flex items-center text-blue-600 hover:text-blue-800 transition duration-200"
                    >
                      <svg
                        class="w-5 h-5 mr-2" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <span class="truncate">{{ attachment.name }}</span>
                    </a>
                    <p class="text-xs text-gray-500 mt-1">{{ getFileSize(attachment.size) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <div v-if="ticket.closed" class="mt-10 border-t border-gray-200 pt-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4 p-6">Kindly review our service</h3>

            <!-- Show existing review if available -->
            <div v-if="ticket.review" class="bg-gray-50 p-6 rounded-lg mb-6">
              <div class="flex items-center mb-4">
                <div class="flex">
                  <svg
                    v-for="star in 5" :key="star" class="w-6 h-6"
                    :class="star <= ticket.review.rating ? 'text-yellow-400' : 'text-gray-300'"
                    fill="currentColor" viewBox="0 0 20 20"
                  >
                    <path
                      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                    />
                  </svg>
                </div>
                <span class="ml-2 text-sm text-gray-500">
                  Reviewed on {{ formatDate(ticket.review.created_at) }}
                </span>
              </div>
              <p class="text-gray-800">{{ ticket.review.comment }}</p>
            </div>

            <!-- Review Form (shown if no review exists) -->
            <div v-else class="bg-gray-50 p-6 rounded-lg">
              <form enctype="multipart/form-data" @submit.prevent="submitReview">
                <div class="mb-4">
                  <label class="block text-gray-700 text-sm font-medium mb-2">Rating</label>
                  <div class="flex items-center space-x-2">
                    <button
                      v-for="star in 5" :key="star" type="button" class="focus:outline-none"
                      @click="review.rating = star"
                    >
                      <svg
                        class="w-8 h-8"
                        :class="star <= review.rating ? 'text-yellow-400' : 'text-gray-300'"
                        fill="currentColor" viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                        />
                      </svg>
                    </button>
                  </div>
                  <p v-if="reviewForm.errors.rating" class="mt-1 text-sm text-red-600">
                    {{
                      reviewForm.errors.rating }}
                  </p>
                </div>

                <div class="mb-4">
                  <label for="comment" class="block text-gray-700 text-sm font-medium mb-2">Your
                    Feedback</label>
                  <textarea
                    id="comment" v-model="review.comment" rows="4"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="How was your experience with our service?"
                  />
                  <p v-if="reviewForm.errors.comment" class="mt-1 text-sm text-red-600">
                    {{
                      reviewForm.errors.comment }}
                  </p>
                </div>

                <button
                  type="submit" :disabled="reviewForm.processing"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <span v-if="!reviewForm.processing">Submit Review</span>
                  <span v-else>Submitting...</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- FAQ Section -->
  <section class="pt-5 pb-8 lg:pt-[20px] lg:pb-[70px] bg-white">
    <div class="container">
      <div class="-mx-4 flex flex-wrap">
        <div class="w-full px-4">
          <div class="mb-12 max-w-[1400px] lg:mb-20">
            <h2 class="mb-4 text-3xl font-bold text-dark sm:text-4xl md:text-[42px] text-center">
              {{ __('Frequently Asked Questions') }}
            </h2>
            <p class="text-lg leading-relaxed text-body-color sm:text-xl sm:leading-relaxed text-center">
              {{ __('Find answers to common questions about our services and our feedback process.') }}
            </p>
          </div>
        </div>
      </div>

      <!-- FAQ Search -->
      <div class="flex w-full items-center justify-center mb-8">
        <div class="flex justify-center w-full" />
      </div>

      <!-- FAQ Categories with Accordions -->
      <div
        v-for="category in faqCategories.data" v-if="faqCategories && faqCategories.data"
        :id="`faq-category-${category.id}`" :key="category.id" class="mb-12"
      >
        <h3 class="text-2xl font-bold mb-6 text-primary border-b pb-2">
          {{ category.name }}
        </h3>

        <div id="accordion-category" data-accordion="open">
          <div
            v-for="(faq, fi) in category.faqs.slice(0, category.showAll ? category.faqs.length : 3)"
            :key="faq.id" class="transition-all duration-300 ease-in-out"
          >
            <h2 :id="`accordion-heading-${category.id}-${faq.id}`">
              <button
                type="button"
                class="flex items-center justify-between w-full p-5 font-medium text-left text-gray-500 border border-gray-200 focus:ring-4 focus:ring-gray-200 hover:bg-gray-100"
                :aria-expanded="faq.active" :aria-controls="`accordion-body-${category.id}-${faq.id}`"
                :class="{
                  'rounded-t-xl': fi === 0,
                  'border-b-0': fi !== (category.faqs.length - 1) && !faq.active,
                  'bg-gray-50': faq.active
                }" @click="toggleFaqActive(category.id, faq.id)"
              >
                <span class="flex items-center">
                  <svg class="w-5 h-5 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="text-left">{{ faq.name }}</span>
                </span>
                <svg
                  class="w-6 h-6 shrink-0 transition-transform duration-200" fill="currentColor"
                  viewBox="0 0 20 20" :class="{ 'rotate-180': faq.active }"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </h2>
            <div
              :id="`accordion-body-${category.id}-${faq.id}`"
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :class="{ 'max-h-0': !faq.active, 'max-h-[1000px]': faq.active }"
              :aria-labelledby="`accordion-heading-${category.id}-${faq.id}`"
            >
              <div
                class="p-5 border border-gray-200" :class="{
                  'border-b-0': fi !== (category.faqs.length - 1),
                  'border-t-0': fi === 0 && !faq.active
                }"
              >
                <p class="text-gray-700" v-html="sanitizeHtml(faq.details)" />
              </div>
            </div>
          </div>
        </div>

        <!-- View More/Less button -->
        <div v-if="category.faqs.length > 3" class="mt-4 text-center">
          <button
            class="text-primary hover:underline font-medium flex items-center justify-center mx-auto"
            :aria-expanded="category.showAll" :aria-controls="`faq-category-${category.id}`"
            @click="toggleShowAll(category)"
          >
            {{ category.showAll ? __('Show Less') : __('Show More') }}
            <span class="ml-2 text-sm">({{ category.faqs.length }})</span>
            <svg
              class="w-4 h-4 ml-1 transition-transform duration-200" fill="currentColor"
              viewBox="0 0 20 20" :class="{ 'rotate-180': category.showAll }"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="faqForm.processing" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>

      <!-- Pagination -->
      <div class="flex justify-center mt-8">
        <nav class="inline-flex rounded-md shadow">
          <button
            :disabled="!faqCategories.prev_page_url"
            class="px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            :class="{ 'opacity-50 cursor-not-allowed': !faqCategories.prev_page_url }"
            @click="previousFaqPage"
          >
            {{ __('Previous') }}
          </button>
          <span
            class="px-3 py-2 border-t border-b border-gray-300 bg-white text-sm font-medium text-gray-700"
          >
            {{ __('Page') }} {{ faqCategories.current_page }} {{ __('of') }} {{ faqCategories.last_page }}
          </span>
          <button
            :disabled="!faqCategories.next_page_url"
            class="px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            :class="{ 'opacity-50 cursor-not-allowed': !faqCategories.next_page_url }" @click="nextFaqPage"
          >
            {{ __('Next') }}
          </button>
        </nav>
      </div>

      <!-- Link to full FAQ page -->
      <div class="text-center mt-8">
        <Link :href="route('faq')" class="text-primary hover:underline font-medium">
          {{ __('View all FAQs') }}
        </Link>
      </div>
    </div>
  </section>

  <!-- Tracking Error Modal -->
  <div
    v-if="showTrackingErrorModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div class="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
      <div class="flex items-center justify-between p-4 border-b">
        <h3 class="text-lg font-semibold text-gray-900">{{ __('Error') }}</h3>
        <button class="text-gray-400 hover:text-gray-600" @click="closeTrackingErrorModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="p-4">
        <p class="text-gray-700">{{ trackingError }}</p>
      </div>
      <div class="flex justify-end p-4 border-t">
        <button
          class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          @click="closeTrackingErrorModal"
        >
          {{ __('Close') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Landing/Layout'
import Icon from '@/Shared/Icon'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import sanitizeHtml from 'sanitize-html'
import UploadAdapter from '@/Shared/UploadAdapter'
import CKEditor from '@ckeditor/ckeditor5-vue'
import { random } from 'lodash'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import { useForm } from '@inertiajs/vue3'
import axios from 'axios'

export default {
    metaInfo: { title: 'Home' },
    components: {
        Icon,
        LoadingButton,
        SelectInput,
        TextInput,
        Head,
        Link,
        ckeditor: CKEditor.component,
    },
    layout: Layout,
    props: {
        all_categories: { required: true },
        departments: Array,
        types: Array,
        title: String,
        page: Object,
        auth: Object,
        trackedTicket: Object,
        faqCategories: Object,
        filters: Object,
        faqCategories: {
            type: Object,
            default: () => ({
                data: [],
                current_page: 1,
                last_page: 1,
                next_page_url: null,
                prev_page_url: null,
                total: 0,
            }),
        },
    },

    data() {
        return {
            ticket: null,
            editor: ClassicEditor,
            html: JSON.parse(this.page.html),
            editorConfig: {
                toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|', 'insertTable', 'blockQuote', '|', 'imageUpload', 'mediaEmbed', '|', 'undo', 'redo'],
                table: {
                    toolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
                extraPlugins: [UploadAdapter],
            },
            activeTab: 'submit',
            currentStep: 1,
            trackingForm: this.$inertia.form({
                reference: '',
            }),
            review: {
                rating: 0,
                comment: '',
            },
            reviewForm: useForm({
                ticket_id: null,
                rating: null,
                comment: '',
            }),
            form: this.$inertia.form({
                is_anonymous: false,
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                subject: '',
                date_of_incident: null,
                details: '',
                files: [],
                consent_checkbox: false,
                department_id: null,
                category_id: null,
                sub_category_id: null,
                type_id: null,
            }),
            dateError: '',
            showTicketModal: false,
            loadingFailed: false,
            trackingError: null,
            showTrackingErrorModal: false,
            faqCategories: this.faqCategories || {
                data: [],
                current_page: 1,
                last_page: 1,
                next_page_url: null,
                prev_page_url: null,
                total: 0,
            },
            faqForm: {
                search: '',
                page: 1,
                processing: false,
            },
            trackingError: this.$page.props.trackingError || null,
            ticket: this.$page.props.trackedTicket || null,
            showTicketModal: !!this.$page.props.trackedTicket,
            showTrackingErrorModal: !!this.$page.props.trackingError,
        }
    },
    watch: {
        'faqForm.search': throttle(function (search) {
            this.$inertia.get(this.route('home'), { search }, {
                replace: true,
                preserveState: true,
                preserveScroll: true,
                only: ['faqCategories'],
            })
        }, 150),
    },

    created() {
        if (this.auth.user) {
            this.form.first_name = this.auth.user.first_name
            this.form.last_name = this.auth.user.last_name
            this.form.email = this.auth.user.email
        }

        // Automatically show modal if trackedTicket is provided
        if (this.trackedTicket) {
            this.ticket = this.trackedTicket
            this.showTicketModal = true
            this.activeTab = 'track'
        }

        // If there's a tracking error, show it
        if (this.trackingError) {
            this.activeTab = 'track'
        }
    },
    methods: {
        validateDate() {
            if (!this.form.date_of_incident) {
                this.dateError = ''
                return
            }

            const today = new Date()
            const localToday = [
                today.getFullYear(),
                String(today.getMonth() + 1).padStart(2, '0'),
                String(today.getDate()).padStart(2, '0'),
            ].join('-')

            if (this.form.date_of_incident > localToday) {
                this.dateError = 'Date cannot be in the future'
            } else {
                this.dateError = ''
            }
        },

        sanitizeHtml(html) {
            return sanitizeHtml(html, {
                allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'h1', 'h2', 'h3']),
                allowedAttributes: {
                    '*': ['class', 'style', 'href', 'target', 'rel'],
                    img: ['src', 'alt', 'width', 'height'],
                },
            })
        },

        // New methods for dynamic text
        getSelectedType() {
            if (!this.form.type_id) return null
            return this.types.find(type => type.id === this.form.type_id)
        },

        getTypeName() {
            const type = this.getSelectedType()
            return type ? type.name : 'Feedback'
        },

        getStepTitle() {
            const typeName = this.getTypeName()
            return this.__('Submit a') + ' ' + typeName
        },

        getSubmitButtonText() {
            const typeName = this.getTypeName()
            return this.__('Submit') + ' ' + typeName
        },

        getSubjectLabel() {
            const typeName = this.getTypeName().toLowerCase()
            if (typeName.includes('complaint')) {
                return this.__('Complaint Subject')
            } else if (typeName.includes('enquiry')) {
                return this.__('Enquiry Subject')
            } else if (typeName.includes('suggestion')) {
                return this.__('Suggestion Title')
            } else {
                return this.__('Subject')
            }
        },

        getDateLabel() {
            const typeName = this.getTypeName().toLowerCase()
            if (typeName.includes('complaint')) {
                return this.__('Date of Incident')
            } else if (typeName.includes('enquiry')) {
                return this.__('Date of Query')
            } else if (typeName.includes('suggestion')) {
                return this.__('Date of Suggestion')
            } else {
                return this.__('Date')
            }
        },

        getDetailsLabel() {
            const typeName = this.getTypeName().toLowerCase()
            if (typeName.includes('complaint')) {
                return this.__('Complaint Details')
            } else if (typeName.includes('enquiry')) {
                return this.__('Enquiry Details')
            } else if (typeName.includes('suggestion')) {
                return this.__('Suggestion Details')
            } else {
                return this.__('Details')
            }
        },

        onTypeChange() {
            this.selectedType = this.getSelectedType()
        },

        toggleFaqActive(categoryId, faqId) {
            this.faqCategories.data.forEach(category => {
                if (category.id === categoryId) {
                    category.faqs.forEach(faq => {
                        if (faq.id === faqId) {
                            faq.active = !faq.active
                        } else {
                            faq.active = false
                        }
                    })
                }
            })
        },

        toggleShowAll(category) {
            category.showAll = !category.showAll
            this.$nextTick(() => {
                const element = document.getElementById(`faq-category-${category.id}`)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                }
            })
        },

        previousFaqPage() {
            if (this.faqCategories.prev_page_url) {
                this.faqForm.page--
                this.loadFaqs()
            }
        },

        nextFaqPage() {
            if (this.faqCategories.next_page_url) {
                this.faqForm.page++
                this.loadFaqs()
            }
        },

        trackComplaint() {
            // If we already have a ticket from props, just show the modal
            if (this.ticket) {
                this.showTicketModal = true
                return
            }

            // Clear previous errors and results
            this.loadingFailed = false
            this.trackingError = null
            this.ticket = null

            // Validate input
            if (!this.trackingForm.reference) {
                this.trackingError = this.__('Please enter a reference number')
                this.showTrackingErrorModal = true
                return
            }

            // Use axios to make a POST request and get JSON response
            axios.post(this.route('track_complaint'), {
                reference: this.trackingForm.reference,
            })
                .then(response => {
                    if (response.data.trackedTicket) {
                        this.ticket = response.data.trackedTicket
                        this.showTicketModal = true
                    } else {
                        this.loadingFailed = true
                        this.trackingError = response.data.trackingError || this.__('No feedback found with that reference number')
                        this.showTrackingErrorModal = true
                    }
                })
                .catch(error => {
                    this.loadingFailed = true
                    if (error.response && error.response.data && error.response.data.trackingError) {
                        this.trackingError = error.response.data.trackingError
                    } else {
                        this.trackingError = this.__('No feedback found with that reference number')
                    }
                    this.showTrackingErrorModal = true
                })
        },

        getStatusClass(statusName) {
            if (!statusName) return 'bg-gray-100 text-gray-800'

            const statusClasses = {
                'Open': 'bg-green-100 text-green-800',
                'Pending': 'bg-yellow-100 text-yellow-800',
                'Resolved': 'bg-blue-100 text-blue-800',
                'Closed': 'bg-gray-100 text-gray-800',
                'In Progress': 'bg-indigo-100 text-indigo-800',
                'Rejected': 'bg-red-100 text-red-800',
            }

            return statusClasses[statusName] || 'bg-gray-100 text-gray-800'
        },

        submitReview() {
            this.validateDate()
            if (this.dateError) {
                return // Stop submission if validation fails
            }
            // Ensure we have the ticket ID
            if (!this.ticket?.id) {
                this.$page.props.flash.error = this.__('No active ticket found')
                return false
            }

            // Prepare the form data
            this.reviewForm.ticket_id = this.ticket.id
            this.reviewForm.rating = this.review.rating
            this.reviewForm.comment = this.review.comment

            return this.reviewForm.post(this.route('ticket.review.store', { ticket: this.ticket.id }), {
                preserveScroll: true,
                onSuccess: () => {
                    // Refresh the ticket data to show the new review
                    if (this.$page.props.trackedTicket) {
                        this.ticket = this.$page.props.trackedTicket
                    }
                    this.review = { rating: 0, comment: '' }
                    this.$page.props.flash.success = this.__('Thank you for your feedback!')
                    return true
                },
                onError: (errors) => {
                    this.$page.props.flash.error = errors.message || this.__('Failed to submit review')
                    return false
                },
            })
        },

        closeModal() {
            this.showTicketModal = false
            this.ticket = null
            this.loadingFailed = false
        },
        getStatusClass(status) {
            const statusClasses = {
                'Open': 'bg-green-100 text-green-800',
                'Pending': 'bg-yellow-100 text-yellow-800',
                'Resolved': 'bg-blue-100 text-blue-800',
                'Closed': 'bg-gray-100 text-gray-800',
                'In Progress': 'bg-indigo-100 text-indigo-800',
                'Rejected': 'bg-red-100 text-red-800',
            }
            return statusClasses[status] || 'bg-gray-100 text-gray-800'
        },
        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleDateString()
        },

        formatDateTime(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleString()
        },

        handleAnonymousChange() {
            this.currentStep = 1
            if (this.form.is_anonymous === true) {
                this.form.first_name = 'anonymous'
                this.form.last_name = 'user'
                this.form.email = 'malverntchitengu@gmail.com'
                this.form.phone_number = 'random-' + random(100000, 999999)
            } else if (this.form.is_anonymous === false && this.auth.user) {
                this.form.first_name = this.auth.user.first_name
                this.form.last_name = this.auth.user.last_name
                this.form.email = this.auth.user.email
            }
        },

        nextStep() {
            if (this.currentStep === 1) {
                if (this.form.is_anonymous === null) {
                    alert(this.__('Please select whether you want to submit anonymously or not'))
                    return
                }
                if (!this.form.type_id) {
                    alert(this.__('Please select a feedback type'))
                    return
                }
            }
            else if (this.currentStep === 2 && !this.form.is_anonymous) {
                if (!this.form.first_name || !this.form.last_name || !this.form.email || !this.form.phone_number) {
                    alert(this.__('Please fill in all your personal details'))
                    return
                }
            }
            else if ((this.currentStep === 2 && this.form.is_anonymous) ||
                (this.currentStep === 3 && !this.form.is_anonymous)) {
                if (!this.form.subject || !this.form.date_of_incident || !this.form.details) {
                    alert(this.__('Please fill in all details'))
                    return
                }

                let totalFileSize = this.form.files.reduce((sum, file) => sum + file.size, 0)
                if (this.form.files.length > 5 || totalFileSize > (20 * 1024 * 1024)) {
                    alert(this.__('Maximum 5 files and total size of 20MB are allowed.'))
                    return
                }
            }

            this.currentStep++
        },

        prevStep() {
            this.currentStep--
        },

        uploader(editor) {
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                return new UploadAdapter(loader)
            }
        },

        fileInputChange(e) {
            const MAX_FILE_SIZE = 5 * 1024 * 1024
            const MAX_TOTAL_SIZE = 20 * 1024 * 1024
            const currentTotal = this.form.files.reduce((sum, file) => sum + file.size, 0)

            for (const file of e.target.files) {
                if (file.size > MAX_FILE_SIZE) {
                    alert(this.__('Error:') + ` "${file.name}" ` + this.__('exceeds 5MB limit per file'))
                    return
                }

                if (currentTotal + file.size > MAX_TOTAL_SIZE) {
                    alert(this.__('Error: Adding') + ` "${file.name}" ` + this.__('would exceed 20MB total limit'))
                    return
                }

                if (this.form.files.length >= 5) {
                    alert(this.__('Maximum 5 files allowed'))
                    return
                }

                const allowedTypes = [
                    'xlsx', 'xls', 'doc', 'docx', 'ppt', 'pptx',
                    'txt', 'pdf', 'zip', 'jpg', 'jpeg', 'png', 'gif',
                ]
                const fileExt = file.name.split('.').pop().toLowerCase()

                if (!allowedTypes.includes(fileExt)) {
                    alert(this.__('File type') + ` .${fileExt} ` + this.__('not allowed'))
                    return
                }

                this.form.files.push(file)
            }

            e.target.value = ''
        },

        fileRemove(file, index) {
            this.form.files.splice(index, 1)
        },

        stripHtml(html) {
            const tmp = document.createElement('div')
            tmp.innerHTML = html
            return tmp.textContent || tmp.innerText || ''
        },

        getFileSize(size) {
            const i = Math.floor(Math.log(size) / Math.log(1024))
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i]
        },

        fileBrowse() {
            this.$refs.file.click()
        },

        store() {
            if (!this.form.consent_checkbox) {
                alert(this.__('You must consent to CAAZ collecting and processing your data'))
                return
            }

            this.form.post(this.route('ticket_store'), {
                onSuccess: () => {
                    this.form.reset()
                    this.currentStep = 1
                    this.selectedType = null
                },
                onError: (errors) => {
                    console.error('Submission error:', errors)
                    if (errors.message) {
                        alert(errors.message)
                    } else {
                        alert(this.__('There was an error submitting your feedback. Please check your inputs.'))
                    }

                    const firstError = Object.keys(errors)[0]
                    if (firstError) {
                        const element = document.querySelector(`[name="${firstError}"]`)
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        }
                    }
                },
            })
        },

        loadFaqs() {
            this.faqForm.processing = true
            this.$inertia.get(this.route('home'),
                {
                    search: this.faqForm.search,
                    page: this.faqForm.page,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    only: ['faqCategories'],
                    onFinish: () => {
                        this.faqForm.processing = false
                    },
                },
            )
        },
        closeTrackingErrorModal() {
            this.showTrackingErrorModal = false
            this.trackingError = null
        },
    },

}
</script>
<style>
/* Add to your CSS */
.modal-content-top {
    scroll-behavior: auto !important;
}
</style>
